import { useCallback } from "react";

import {
  queryOptions,
  useQueryClient,
  useQuery,
  useMutation,
} from "@tanstack/react-query";

import { store } from "../store";
import * as storiesApi from "@app/services/stories";
import { useGeneratePersonas, useGenerateVisuals } from "./zoltar";

export const storyKey = {
  all: ["stories"],
  lists: () => [...storyKey.all, "list"],
  list: (filters) => [...storyKey.lists(), { filters }],
  details: () => [...storyKey.all, "detail"],
  detail: (id) => [...storyKey.details(), id],
};

export const getStoriesKey = (...keys) => ["stories", ...keys];

const storiesQueryOptions = () =>
  queryOptions({
    queryKey: storyKey.lists(),
    queryFn: ({ signal }) => storiesApi.getStories({ signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // onSuccess: (data) => {
    //   store.getState().setStories(data.stories);
    // },
    meta: {
      errorMessage: "Failed to load stories. Please try again.",
    },
  });

export const storyQueryOptions = (storyId) =>
  queryOptions({
    queryKey: storyKey.detail(storyId),
    queryFn: ({ signal }) => storiesApi.getStory(storyId, { signal }),
    meta: {
      errorMessage: "Failed to load the story. Please try again.",
    },
    enabled: !!storyId,
  });

export const useStories = (options = {}) => {
  const query = useQuery({
    ...storiesQueryOptions(),
    ...options,
  });

  return query;
};

export const useSimpleStory = (storyId) => {
  const select = useCallback(
    (data) => data?.find((s) => s.id === storyId),
    [storyId],
  );

  const query = useStories({
    select,
  });

  return query;
};

export const useStory = (storyId, options = {}) => {
  const query = useQuery({
    ...storyQueryOptions(storyId),
    ...options,
  });

  return query;
};

export const useUpdateStory = (storyId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: storyKey.detail(storyId),
    mutationFn: (variables, { signal }) =>
      storiesApi.updateStory(storyId, variables, { signal }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: storyKey.all });

      const previousData = queryClient.getQueryData(storyKey.detail(storyId));
      const previousListData = queryClient.getQueryData(storyKey.lists());

      queryClient.setQueryData(storyKey.detail(storyId), {
        ...previousData,
        ...variables,
      });
      queryClient.setQueryData(storyKey.lists(), (old) =>
        old.map((story) =>
          story.id === storyId ? { ...story, ...variables } : story,
        ),
      );

      return {
        previousData,
        previousListData,
      };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(storyKey.detail(storyId), data.story);
      queryClient.invalidateQueries({ queryKey: storyKey.lists() });
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(storyKey.detail(storyId), context.previousData);
      queryClient.setQueryData(storyKey.lists(), context.previousListData);

      // TODO: handle error
    },
    enabled: !!storyId,
  });

  return mutation;
};

export const useCreateStory = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (variables, { signal }) =>
      storiesApi.createStory(variables, { signal }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: storyKey.lists() });

      if (!variables.id) {
        variables.id = self.crypto.randomUUID();
      }

      const optimisticStory = {
        ...variables,
      };
      const previousData = queryClient.getQueryData(storyKey.lists());

      queryClient.setQueryData(storyKey.lists(), [
        ...previousData,
        optimisticStory,
      ]);
      queryClient.setQueryData(
        storyKey.details(optimisticStory.id),
        optimisticStory,
      );

      return {
        optimisticStory,
        previousData,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: storyKey.lists() });
      queryClient.setQueryData(storyKey.details(data.id), data);
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(storyKey.lists(), context.previousData);
      queryClient.removeQueries({
        queryKey: storyKey.details(context.optimisticStory.id),
      });

      // TODO: handle error
    },
  });

  return mutation;
};

export const useDeleteStory = (storyId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (variables, { signal }) =>
      storiesApi.deleteStory(storyId, { signal }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: storyKey.lists() });

      const previousData = queryClient.getQueryData(storyKey.details(storyId));
      const oldStories = queryClient.getQueryData(storyKey.lists());
      const previousIndex = oldStories.findIndex(
        (story) => story.id === storyId,
      );

      queryClient.setQueryData(storyKey.lists(), (old) =>
        old.filter((story) => story.id !== storyId),
      );

      queryClient.removeQueries({ queryKey: storyKey.details(storyId) });

      return { previousData, previousIndex };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storyKey.lists() });
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(storyKey.lists(), (state) =>
        state.toSpliced(context.previousIndex, 0, context.previousData),
      );
    },
  });

  return mutation;
};

export const useShareStory = (storyId) => {
  const queryClient = useQueryClient();
  const updateStory = useUpdateStory(storyId);

  const mutation = useMutation({
    mutationFn: (variables) => {
      const previousData = queryClient.getQueryData(storyKey.detail(storyId));
      const sharedWith = (previousData?.sharedWith || []).concat(
        variables.email,
      );

      return updateStory.mutateAsync({ sharedWith });
    },
    onSuccess: (data, variables) => {
      store.getState().addToast(`Shared with ${variables.email}`, {
        variant: "positive",
        timeout: 5000,
      });
    },
    onError: (error, variables, context) => {
      store
        .getState()
        .addToast(`Failed to share with ${variables.email}. Please try again`, {
          variant: "negative",
          timeout: 10 * 1000,
          actionLabel: "Retry",
          shouldCloseOnAction: true,
          onAction: () => {
            mutation.mutate(variables);
          },
        });
    },
  });

  return mutation;
};

export const useUnshareStory = (storyId) => {
  const queryClient = useQueryClient();
  const updateStory = useUpdateStory(storyId);

  const mutation = useMutation({
    mutationFn: (variables) => {
      const previousData = queryClient.getQueryData(storyKey.detail(storyId));
      const sharedWith = (previousData?.sharedWith || []).filter(
        (email) => email !== variables.email,
      );

      return updateStory.mutateAsync({ sharedWith });
    },
  });

  return mutation;
};

export const useGenerateImages = (storyId) => {
  const queryClient = useQueryClient();
  const generatePersonas = useGeneratePersonas(storyId);
  const generateVisuals = useGenerateVisuals(storyId);
  const updateStory = useUpdateStory(storyId);

  const mutation = useMutation({
    mutationFn: async (variables = {}) => {
      const { forceGenerate = false } = variables;
      const story = queryClient.getQueryData(storyKey.detail(storyId));

      if (!story) {
        throw new Error(`Story with id ${storyId} not found`);
      }

      const personasPromises = [];
      const visualsPromises = [];

      // Check if personas need to be generated
      if (story.personas?.some((p) => !p.avatarPrompt) || forceGenerate) {
        personasPromises.push(generatePersonas.mutateAsync());
      }

      // Check if visuals need to be fetched
      if (
        story.script?.some((step) => !step.visuals?.length) ||
        forceGenerate
      ) {
        visualsPromises.push(generateVisuals.mutateAsync());
      }

      // Wait for all generation to complete before saving
      await Promise.all([...personasPromises, ...visualsPromises]);

      // Get the updated story from cache (includes all generated images)
      const updatedStory = queryClient.getQueryData(storyKey.detail(storyId));

      // Save the complete story to the server only once at the end
      await updateStory.mutateAsync({
        personas: updatedStory.personas,
        script: updatedStory.script,
      });

      return { success: true };
    },
    onSuccess: () => {
      store.getState().addToast("Generation completed", {
        variant: "positive",
        timeout: 1000 * 60, // 1 minute
        actionLabel: "Create Presentation",
        onAction: () => {
          console.log("Create Presentation"); // TODO: set({ isExpressDialogOpen: true });
        },
      });
    },
    onError: (error) => {
      console.error(`Error generating images for story ${storyId}:`, error);
      store
        .getState()
        .addToast("Failed to generate images. Please try again.", {
          variant: "negative",
          timeout: 10 * 1000,
        });
    },
  });

  return mutation;
};
